<template>
  <section
    id="icon-pack-playground"
    :class="['playground padding-top-5xl padding-bottom-2xl', { inverted: previewMode == 'night' }]"
    :style="componentStyle"
  >
    <div class="container">
      <div class="playground-wrapper display-flex flex-content-center">
        <div
          class="playground-icons display-flex flex-row flex-nowrap flex-items-end flex-content-center"
          style="gap: var(--icon-pack-playground-spacing, 5%); font-size: var(--icon-pack-playground-size, var(--_icon-pack-playground-size))"
        >
          <span
            v-for="(icon, index) in featuredIcons"
            :key="`${icon}-${index}-${offset}`"
            class="playground-icon-container"
          >
            <transition
              name="plaground-icon-update"
              enter-active-class="animate__animated animate__faster animate__bounceIn"
              appear
            >
              <i
                :class="[`${familyStyleByIndex(index)} fa-${icon} ${animationStyle} fa-fw playground-icon`]"
                :style="`font-size: var(--icon-pack-playground-size, calc(${selectedSize} * 1em));`"
              ></i>
            </transition>
          </span>
        </div>
      </div>

      <div
        class="playground-controls margin-top-4xl"
        :style="`
          --button-background: none;
          --button-font-weight: var(--font-weight-normal);
          --button-hover-background: none;
          --button-hover-background: var(--theme-button-hover-background);
          --button-hover-border-color: var(--theme-button-hover-border-color);
          --button-margin-bottom: 0;
          --button-padding: var(--spacing-sm) var(--spacing-md);
          --form-button-margin: 0;
          --input-background: transparent;
          --input-border-color: var(--border-color);
          --input-border-radius: var(--button-border-radius);
          --input-min-height: 3.25em;
          --input-with-icon-color: initial;
          --label-color: initial;
          --label-font-weight: var(--font-weight-normal);
          --link-color: initial;
        `"
      >
        <form
          class="display-flex flex-items-center flex-content-center flex-wrap"
          style="gap: var(--spacing-xl)"
        >
          <div
            class="display-flex flex-items-center playground-control-size padding-y-sm padding-x-md with-border"
            style="border-radius: var(--border-radius-sm); gap: var(--spacing-sm)"
          >
            <label
              for="playground-control-size-input"
              class="text-nowrap margin-0"
              >Icon Size</label
            >
            <input
              id="playground-control-size-input"
              v-model="size"
              list="playground-control-size"
              max="6"
              min="0"
              style="width: auto"
              type="range"
              value="4"
            />

            <!-- <datalist id="playground-control-size">
              <option
                v-for="(size, index) in sizes"
                :key="index"
                :value="size"
              ></option>
            </datalist> -->
          </div>

          <wa-select-custom
            :custom-select-style="selectCustomSelectStyle"
            :is-full-width="false"
            :options="animationOptions"
            :value="animation"
            class="playground-control-animation"
            icon-name="faSwatchbook"
            is-minimal
            label="Animation"
            @change="(value) => updateAnimation(value)"
          />

          <button
            class="flat with-border playground-control-swap-icons"
            @click.prevent="swapIcons()"
          >
            <font-awesome-icon
              :icon="$v6.solid.faSwap"
              class="margin-right-3xs"
              size="lg"
              fixed-width
            />
            Swap Icons
          </button>

          <button
            class="flat with-border playground-control-shuffle-styles"
            @click.prevent="shuffleStyles()"
          >
            <font-awesome-icon
              :icon="$v6.solid.faShuffle"
              class="margin-right-3xs"
              size="lg"
              fixed-width
            />
            Shuffle Styles
          </button>

          <button
            v-if="previewMode === 'day'"
            aria-label="Nox!"
            data-balloon-pos="up"
            class="flat with-border playground-control-invert"
            @click.prevent="togglePreviewMode('night')"
          >
            <font-awesome-icon
              :icon="$v6.solid.faMoon"
              size="lg"
              fixed-width
            />
            Invert
          </button>

          <button
            v-else
            aria-label="Lumos!"
            data-balloon-pos="up"
            class="flat with-border playground-control-invert"
            @click.prevent="togglePreviewMode('day')"
          >
            <font-awesome-icon
              :icon="$v6.solid.faSun"
              size="lg"
              fixed-width
            />
            Invert
          </button>

          <!-- TODO: DRY this out -->
          <!-- TODO: simplify disco mode - maybe follow what the factory does -->
          <!-- <button
            v-if="disco"
            aria-label="Stop the Party!"
            data-balloon-pos="up"
            class="flat with-border playground-control-disco"
            @click.prevent="stopTheParty()"
          >
            <font-awesome-icon
              :icon="$v6.solid.faHand"
              size="lg"
              fixed-width
            />
            <span class="sr-only">Stop the Party!</span>
          </button>
          <button
            v-else
            aria-label="Disco Mode!"
            data-balloon-pos="up"
            class="flat with-border playground-control-disco"
            @click.prevent="startTheDisco()"
          >
            <font-awesome-icon
              :icon="$v6.solid.faPartyHorn"
              size="lg"
              fixed-width
            />
            <span class="sr-only">Disco Mode!</span>
          </button>
          -->
        </form>
      </div>
    </div>
  </section>
</template>

<script>
  import { animationOptions, getShortCode } from 'app/releases.js'
  import { shuffle, take, last } from 'lodash'

  const DEFAULT_SIZE_INDEX = 4
  const FEATURED_ICON_COUNT = 7

  export default {
    name: 'PackPlayground',

    inject: ['iconPack'],

    data() {
      return {
        animation: '',
        featuredIcons: [],
        iconName: 'house',
        isDiscoModeOn: false,
        offset: 0,
        previewMode: 'day',
        size: DEFAULT_SIZE_INDEX,
        sizes: ['0.5', '0.75', '1', '2', '3', '4', '5'],
        version: 6
      }
    },

    computed: {
      animationOptions() {
        return animationOptions(this.version)
      },

      animationStyle() {
        if (this.animation === 'spin-reverse') {
          return ` fa-spin fa-spin-reverse`
        }

        if (this.animation) {
          return ` fa-${this.animation}`
        }

        return ''
      },

      componentStyle() {
        let style = {}

        style['--_icon-pack-playground-size'] = '2rem'
        style['--input-range-accent-color'] = 'var(--theme-action-primary-dk)'
        style['overflow'] = 'hidden'

        if (this.previewMode == 'night') {
          style['--balloon-color'] = 'var(--fa-dk-navy)'
        } else {
          style['background'] = 'var(--white)'
        }

        return {
          ...style
        }
      },

      iconBaseSize() {
        return this.iconPack().canvasSize * (1 / 16)
      },

      iconLargestSize() {
        return last(this.sizes)
      },

      selectCustomSelectStyle() {
        return {
          'font-weight': 'var(--font-weight-normal)',
          'padding-top': 'var(--spacing-sm)',
          'padding-bottom': 'var(--spacing-sm)',
          'color': 'var(--body-color)'
        }
      },

      selectedSize() {
        return `${this.sizes[this.size]}`
      }
    },

    created() {
      this.getIcons()
    },

    methods: {
      calculateIndex(index) {
        const familyStylesCount = this.iconPack().featuredFamilyStyles.length
        return (index + this.offset) % familyStylesCount
      },

      doTheDisco() {
        if (this.isDiscoModeOn) {
          setTimeout(this.doTheDisco, 400)
          this.shuffleStyles()
          this.animation = 'beat'
          this.previewMode = this.previewMode === 'night' ? 'day' : 'night'
        }
      },

      familyStyleByIndex(index) {
        const selectedFamilyStyle = this.iconPack().featuredFamilyStyles[this.calculateIndex(index)]
        return getShortCode(selectedFamilyStyle.family, selectedFamilyStyle.style)
      },

      getIcons() {
        this.featuredIcons = take(shuffle(this.iconPack().featuredIcons), FEATURED_ICON_COUNT)
      },

      shuffleStyles() {
        this.$posthog.capture('pack_landing:playground_button_click', { pack: this.iconPack().name, button: 'shuffle style' })
        this.offset = this.offset + 1
        if (this.offset > FEATURED_ICON_COUNT - 1) {
          this.offset = 0
        }
      },

      startTheDisco() {
        this.$posthog.capture('pack_landing:playground_button_click', { pack: this.iconPack().name, button: 'disco mode' })
        this.isDiscoModeOn = true
        setTimeout(this.doTheDisco, 400)
      },

      stopTheParty() {
        this.previewMode = 'day'
        this.animation = ''
        this.isDiscoModeOn = false
      },

      swapIcons() {
        this.$posthog.capture('pack_landing:playground_button_click', { pack: this.iconPack().name, button: 'swap icons' })
        this.getIcons()
        this.stopTheParty()
      },

      togglePreviewMode(mode) {
        this.$posthog.capture('pack_landing:playground_button_click', { pack: this.iconPack().name, button: 'invert' })
        this.previewMode = mode
      },

      updateAnimation(value) {
        this.stopTheParty()
        this.$posthog.capture('pack_landing:playground_button_click', { pack: this.iconPack().name, button: 'animate', animation: value })
        this.animation = value
      }
    }
  }
</script>
